import { useEffect, useRef, useState } from 'react';
import './styles.scss';
import { VGSFieldState, VGSForm } from '../../interface/checkoutInterface';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import { setVGSForm } from '../../redux/slices/vgsFormSlice';
import { PaymentProps as PaymentFieldProps } from '../payment/Payment';
import {
  getBorderProps,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { loadExternalScript } from '../../utils/loadExternalScript';

declare global {
  export interface Window {
    VGSCollect?: {
      create: (
        _vaultId: string,
        _environment: string,
        _callback?: (_state: string) => void,
      ) => VGSForm;
    };
  }
}

type IDesktop = 'desktop' | 'tablet' | 'mobile';

interface CollectFormProps {
  fields: PaymentFieldProps;
  currentDeviceType: string;
  editMode: boolean | undefined;
}

const CollectForm = ({
  useAppSelector,
  useAppDispatch,
  fields,
  currentDeviceType,
}: ReduxSelectorProps & ReduxDispatchProps & CollectFormProps) => {
  const [error, setError] = useState<{
    holder: VGSFieldState | null;
    number: VGSFieldState | null;
    CVC: VGSFieldState | null;
    expirationDate: VGSFieldState | null;
  }>({
    holder: null,
    number: null,
    CVC: null,
    expirationDate: null,
  });
  const [isscriptLoaded, scriptLoaded] = useState(false);
  const { isShowNameOnCard, paymentPlaceholder, heightField } = fields;

  const styles = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI"',
  };

  const height = heightField?.height?.[currentDeviceType as IDesktop];
  const columnGap = heightField?.colGap?.[currentDeviceType as IDesktop];
  const rowGap = heightField?.rowGap?.[currentDeviceType as IDesktop];

  const { isVGSFormError } = useAppSelector(state => state.checkout);
  const { VGSForm } = useAppSelector(state => state.vgsForm);
  const { IsCardEncryption, InBoundRouteId, CardEncryptionVaultId } =
    useAppSelector(state => state.store.storeData);
  const dispatch = useAppDispatch();

  const VgsEnvironment = process.env.REACT_APP_VGS_ENVIRONMENT || 'sandbox';

  const [isVGSInitialized, setIsVGSInitialized] = useState(false);

  const [isNumberFocused, setIsNumberFocused] = useState(false);
  const [isCVCFocused, setIsCVCFocused] = useState(false);
  const [isExpFocused, setIsExpFocused] = useState(false);
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const fieldRef = useRef<HTMLDivElement | null>(null);

  const waitForDOMElements = (
    selectors: string[],
    maxRetries = 50,
  ): Promise<boolean> => {
    return new Promise(resolve => {
      let retries = 0;

      const checkElements = () => {
        const allElementsExist = selectors.every(selector => {
          const element = document.querySelector(selector);
          return element !== null;
        });

        if (allElementsExist) {
          resolve(true);
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(checkElements, 100);
        } else {
          resolve(false);
        }
      };

      checkElements();
    });
  };

  const initializeVGSForm = async () => {
    if (isVGSInitialized) {
      return;
    }
    try {
      if (!CardEncryptionVaultId) {
        throw new Error('CardEncryptionVaultId is required but not provided');
      }
      if (!InBoundRouteId) {
        throw new Error('InBoundRouteId is required but not provided');
      }
      if (!window?.VGSCollect?.create) {
        throw new Error('VGS Collect library is not loaded');
      }

      // Determine required selectors based on configuration
      const requiredSelectors = [
        '#cc-number',
        '#cc-expiration-date',
        '#cc-cvc',
      ];
      if (isShowNameOnCard) {
        requiredSelectors.push('#cc-holder');
      }
      const domReady = await waitForDOMElements(requiredSelectors);
      if (!domReady) {
        throw new Error('Required DOM elements not found');
      }

      const vgsForm = window.VGSCollect.create(
        CardEncryptionVaultId,
        VgsEnvironment,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _state => {},
      ).setRouteId(InBoundRouteId);

      if (isShowNameOnCard) {
        const ccHolder = vgsForm.field('#cc-holder', {
          type: 'text',
          name: 'card_holder',
          placeholder: paymentPlaceholder?.Name?.placeholder,
          validations: ['required'],
          css: {
            ...styles,
            '::placeholder': { color: paymentPlaceholder?.Name?.color },
            color: paymentPlaceholder?.Name?.textColor?.isInherit
              ? 'inherit !important'
              : `${paymentPlaceholder?.Name?.textColor?.color} !important`,
          },
        });
        ccHolder?.on('update', (event: VGSFieldState) => {
          setError(prev => ({
            ...prev,
            holder: event,
          }));
        });
        ccHolder?.on('focus', () => setIsNameFocused(true));
        ccHolder?.on('blur', () => setIsNameFocused(false));
      }
      const number = vgsForm.field('#cc-number', {
        type: 'card-number',
        name: 'card_number',
        successColor: '#4F8A10',
        errorColor: '#D8000C',
        placeholder: paymentPlaceholder?.Number?.placeholder,
        showCardIcon: true,
        validations: ['required', 'validCardNumber'],
        css: {
          ...styles,
          '::placeholder': { color: paymentPlaceholder?.Number?.color },
          color: paymentPlaceholder?.Number?.textColor?.isInherit
            ? 'inherit !important'
            : `${paymentPlaceholder?.Number?.textColor?.color} !important`,
        },
      });

      // Listen for focus and blur events
      number?.on('focus', () => setIsNumberFocused(true));
      number?.on('blur', () => setIsNumberFocused(false));

      const cvcField = vgsForm.field('#cc-cvc', {
        type: 'card-security-code',
        name: 'card_cvc',
        successColor: '#4F8A10',
        errorColor: '#D8000C',
        placeholder: paymentPlaceholder?.CVV?.placeholder,
        validations: ['required', 'validCardSecurityCode'],
        css: {
          ...styles,
          '::placeholder': { color: paymentPlaceholder?.CVV?.color },
          color: paymentPlaceholder?.CVV?.textColor?.isInherit
            ? 'inherit !important'
            : `${paymentPlaceholder?.CVV?.textColor?.color} !important`,
        },
      });
      cvcField?.on('focus', () => setIsCVCFocused(true));
      cvcField?.on('blur', () => setIsCVCFocused(false));

      const expirationDate = vgsForm.field('#cc-expiration-date', {
        type: 'card-expiration-date',
        name: 'card_exp',
        successColor: '#4F8A10',
        errorColor: '#D8000C',
        placeholder: paymentPlaceholder?.Exp.placeholder,
        validations: ['required', 'validCardExpirationDate'],
        css: {
          ...styles,
          '::placeholder': { color: paymentPlaceholder?.Exp?.color },
          color: paymentPlaceholder?.Exp?.textColor?.isInherit
            ? 'inherit !important'
            : `${paymentPlaceholder?.Exp?.textColor?.color} !important`,
        },
      });
      expirationDate?.on('update', (event: VGSFieldState) => {
        setError(prev => ({
          ...prev,
          expirationDate: event,
        }));
      });
      cvcField?.on('update', (event: VGSFieldState) => {
        setError(prev => ({
          ...prev,
          CVC: event,
        }));
      });
      expirationDate.on('focus', () => setIsExpFocused(true));
      expirationDate.on('blur', () => setIsExpFocused(false));

      number?.on('update', (event: VGSFieldState) => {
        setError(prev => ({
          ...prev,
          number: event,
        }));
      });

      dispatch(setVGSForm(vgsForm));
      setIsVGSInitialized(true);
    } catch (error) {
      console.error('Error initializing VGS Collect:', error);
    }
  };
  const isInViewport = (element: HTMLElement | null) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  useEffect(() => {
    if (!fieldRef.current) return;

    const visible = isInViewport(fieldRef.current);
    setIsVisible(visible);
  }, []);

  useEffect(() => {
    const handleUserInteraction = () => {
      setUserInteracted(true);

      window.removeEventListener('mousemove', handleUserInteraction);
      window.removeEventListener('pointerdown', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('focus', handleUserInteraction, true);
      window.removeEventListener('scroll', handleUserInteraction);
    };

    window.addEventListener('mousemove', handleUserInteraction);
    window.addEventListener('pointerdown', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);
    window.addEventListener('focus', handleUserInteraction, true);
    window.addEventListener('scroll', handleUserInteraction, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleUserInteraction);
      window.removeEventListener('pointerdown', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('focus', handleUserInteraction, true);
      window.removeEventListener('scroll', handleUserInteraction);
    };
  }, []);

  useEffect(() => {
    const loadVgs = async () => {
      try {
        await loadExternalScript(
          'https://js.verygoodvault.com/vgs-collect/3.0.0/vgs-collect.js',
          'vgs-collect-script',
        );

        scriptLoaded(true);
      } catch (error) {
        console.error('Failed to load VGS script:', error);
        scriptLoaded(false);
      }
    };

    if ((userInteracted || isVisible) && !isscriptLoaded) {
      loadVgs();
    }

    return () => {
      const existingScript = document.getElementById('vgs-collect-script');
      if (existingScript) {
        existingScript.remove();
      }
      scriptLoaded(false);
    };
  }, [userInteracted, isVisible]);

  useEffect(() => {
    if (
      isscriptLoaded &&
      InBoundRouteId &&
      CardEncryptionVaultId &&
      IsCardEncryption &&
      window?.VGSCollect?.create &&
      !isVGSInitialized
    ) {
      const initTimer = setTimeout(() => {
        initializeVGSForm();
      }, 300);

      return () => {
        clearTimeout(initTimer);
      };
    }
  }, [
    isscriptLoaded,
    InBoundRouteId,
    CardEncryptionVaultId,
    IsCardEncryption,
    isVGSInitialized,
    isShowNameOnCard,
  ]);

  const getErrorMessage = (errorCode: number | undefined, name: string) => {
    switch (errorCode) {
      case 1001:
        return `${name} is required`;
      default:
        return `${name} is not valid`;
    }
  };
  return (
    <div
      ref={fieldRef}
      className="grid"
      style={{
        gap: isNaN(Number(rowGap)) ? rowGap : Number(rowGap),
      }}>
      {/* <p className="text-sm font-normal mb-2" style={{ color: '#6B7280' }}>
        All transactions are secured and encrypted
      </p> */}
      {/* <label>Card number</label> */}
      <div className="form-field-wrapper">
        {paymentPlaceholder?.Number?.headers &&
          paymentPlaceholder?.Number?.headers?.hide && (
            <div
              style={{
                ...getPaddingStyles(
                  paymentPlaceholder?.Number?.headers?.padding,
                  currentDeviceType || 'desktop',
                ),
              }}
              className="text-sm mb-1 ml-2"
              dangerouslySetInnerHTML={{
                __html: paymentPlaceholder?.Number?.headers?.text,
              }}
            />
          )}
        {!isVGSInitialized ? (
          <div className="space-y-1 w-full " id="cc-number">
            <div
              className="h-12 w-full skeleton-shimmer rounded-md"
              style={{
                height: height
                  ? isNaN(+height)
                    ? height
                    : `${+height}px`
                  : '48px',
              }}></div>
          </div>
        ) : (
          <div
            id="cc-number"
            className="form-field"
            style={{
              height: height
                ? isNaN(+height)
                  ? height
                  : `${+height}px`
                : '48px',
              ...getBorderProps(
                isNumberFocused
                  ? paymentPlaceholder?.Number?.outline
                  : paymentPlaceholder?.Number?.border,
                currentDeviceType,
              ),
              backgroundColor: paymentPlaceholder?.Number?.textBackgroundColor
                ?.isInherit
                ? 'inherit'
                : paymentPlaceholder?.Number?.textBackgroundColor?.color,
            }}
          />
        )}
        {isVGSFormError &&
          error?.number &&
          !error?.number?.isValid &&
          error?.number?.errors?.length > 0 && (
            <div className="error-message text-sm text-red-500 ">
              {getErrorMessage(
                VGSForm?.state?.card_number?.errors?.[0]?.code,
                'Card number',
              )}
            </div>
          )}
      </div>
      <div
        className="grid grid-cols-2"
        style={{
          gap: isNaN(Number(columnGap)) ? columnGap : Number(columnGap),
        }}>
        <div className="form-field-wrapper">
          {paymentPlaceholder?.Number?.headers &&
            paymentPlaceholder?.Number?.headers?.hide && (
              <div
                style={{
                  ...getPaddingStyles(
                    paymentPlaceholder?.Exp?.headers?.padding,
                    currentDeviceType || 'desktop',
                  ),
                  ...getNewMarginStyles(
                    paymentPlaceholder?.Exp?.headers?.margin,
                    currentDeviceType || 'desktop',
                  ),
                }}
                className="text-sm mb-1 ml-2"
                dangerouslySetInnerHTML={{
                  __html: paymentPlaceholder?.Exp?.headers?.text,
                }}
              />
            )}
          <div>
            {/* <label>Exp. Date</label> */}
            {!isVGSInitialized ? (
              <div className="space-y-1" id="cc-expiration-date">
                <div className="w-full">
                  <div
                    className="h-12 rounded-md w-full skeleton-shimmer"
                    style={{
                      height: height
                        ? isNaN(+height)
                          ? height
                          : `${+height}px`
                        : '48px',
                    }}></div>
                </div>
              </div>
            ) : (
              <div
                id="cc-expiration-date"
                className="form-field"
                style={{
                  height: height
                    ? isNaN(+height)
                      ? height
                      : `${+height}px`
                    : '48px',
                  ...getBorderProps(
                    isExpFocused
                      ? paymentPlaceholder?.Exp?.outline
                      : paymentPlaceholder?.Exp?.border,
                    currentDeviceType,
                  ),
                  backgroundColor: paymentPlaceholder?.Exp?.textBackgroundColor
                    ?.isInherit
                    ? 'inherit'
                    : paymentPlaceholder?.Exp?.textBackgroundColor?.color,
                }}></div>
            )}
            {isVGSFormError &&
              error?.expirationDate &&
              !error?.expirationDate?.isValid &&
              error?.expirationDate?.errors?.length > 0 && (
                <div className="error-message text-sm text-red-500 ">
                  {getErrorMessage(
                    VGSForm?.state?.card_exp?.errors?.[0]?.code,
                    'Expiration date',
                  )}
                </div>
              )}
          </div>
        </div>
        <div>
          {/* <label>CVV/CVC</label> */}
          <div className="form-field-wrapper">
            {paymentPlaceholder?.CVV?.headers &&
              paymentPlaceholder?.CVV?.headers?.hide && (
                <div
                  style={{
                    ...getPaddingStyles(
                      paymentPlaceholder?.CVV?.headers?.padding,
                      currentDeviceType || 'desktop',
                    ),
                    ...getNewMarginStyles(
                      paymentPlaceholder?.CVV?.headers?.margin,
                      currentDeviceType || 'desktop',
                    ),
                  }}
                  className="text-sm mb-1 ml-2"
                  dangerouslySetInnerHTML={{
                    __html: paymentPlaceholder?.CVV?.headers?.text,
                  }}
                />
              )}
            {!isVGSInitialized ? (
              <div className="space-y-1" id="cc-cvc">
                <div className="w-full">
                  <div
                    className="h-12 rounded-md w-full skeleton-shimmer"
                    style={{
                      height: height
                        ? isNaN(+height)
                          ? height
                          : `${+height}px`
                        : '48px',
                    }}></div>
                </div>
              </div>
            ) : (
              <div
                id="cc-cvc"
                className="form-field"
                style={{
                  height: height
                    ? isNaN(+height)
                      ? height
                      : `${+height}px`
                    : '48px',
                  ...getBorderProps(
                    isCVCFocused
                      ? paymentPlaceholder?.CVV?.outline
                      : paymentPlaceholder?.CVV?.border,
                    currentDeviceType,
                  ),
                  backgroundColor: paymentPlaceholder?.CVV?.textBackgroundColor
                    ?.isInherit
                    ? 'inherit'
                    : paymentPlaceholder?.CVV?.textBackgroundColor?.color,
                }}></div>
            )}
            {isVGSFormError &&
              error?.CVC &&
              !error?.CVC?.isValid &&
              error?.CVC?.errors?.length > 0 && (
                <div className="error-message text-sm text-red-500 ">
                  {getErrorMessage(
                    VGSForm?.state?.card_cvc?.errors?.[0]?.code,
                    'Security code',
                  )}
                </div>
              )}
          </div>
        </div>
      </div>
      {isShowNameOnCard && (
        <div className="form-field-wrapper">
          {paymentPlaceholder?.Name?.headers &&
            paymentPlaceholder?.Name?.headers?.hide && (
              <div
                style={{
                  ...getPaddingStyles(
                    paymentPlaceholder?.Name?.headers?.padding,
                    currentDeviceType || 'desktop',
                  ),
                  ...getNewMarginStyles(
                    paymentPlaceholder?.Name?.headers?.margin,
                    currentDeviceType || 'desktop',
                  ),
                }}
                className="text-sm mb-1 ml-2"
                dangerouslySetInnerHTML={{
                  __html: paymentPlaceholder?.Name?.headers?.text,
                }}
              />
            )}
          {!isVGSInitialized ? (
            <div className="space-y-1 w-full" id="cc-holder">
              <div
                className="h-12 w-full skeleton-shimmer rounded-md"
                style={{
                  height: height
                    ? isNaN(+height)
                      ? height
                      : `${+height}px`
                    : '48px',
                }}></div>
            </div>
          ) : (
            <div
              id="cc-holder"
              className="form-field"
              style={{
                height: height
                  ? isNaN(+height)
                    ? height
                    : `${+height}px`
                  : '48px',
                ...getBorderProps(
                  isNameFocused
                    ? paymentPlaceholder?.Name?.outline
                    : paymentPlaceholder?.Name?.border,
                  currentDeviceType,
                ),
                backgroundColor: paymentPlaceholder?.Name?.textBackgroundColor
                  ?.isInherit
                  ? 'inherit'
                  : paymentPlaceholder?.Name?.textBackgroundColor?.color,
              }}></div>
          )}
          {isVGSFormError &&
            error?.holder &&
            !error?.holder?.isValid &&
            error?.holder?.errors?.length > 0 && (
              <div className="error-message text-sm text-red-500 ">
                {getErrorMessage(
                  VGSForm?.state?.card_holder?.errors?.[0]?.code,
                  'Card holder name',
                )}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default CollectForm;
